import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { alertarErro } from '../helpers/alerts';

// Orçamento/cotação pelo Fio, na conversa que já está aberta.
//
// No portal comercial o vendedor preenche quatro coisas: o modo, o número, o
// TELEFONE do cliente e a SENHA dele — e no fim sai do sistema pra abrir o
// WhatsApp e colar o link. Aqui sobra uma: o número. O telefone é a conversa, a
// identidade é quem está logado, e o link sai como mensagem no histórico.
//
// O que o cliente recebe é o MESMO link do comercial: gerado lá, assinado e com
// validade. O Fio não gera PDF nem link — se gerasse, existiriam duas verdades
// sobre o que o cliente abriu.

const API = process.env.REACT_APP_API_URL || 'http://localhost:5007';

const MODOS = [
  { v: 'orcamento', t: 'Orçamento', icone: 'bi-file-earmark-text' },
  { v: 'cotacao', t: 'Cotação', icone: 'bi-clipboard-check' },
  { v: 'ambos', t: 'Os dois', icone: 'bi-files' },
];

export default function EnviarOrcamentoModal({ aberto, conversaId, nomeDoCliente, onClose, onEnviado }) {
  const [modo, setModo] = useState('orcamento');
  const [numped, setNumped] = useState('');
  const [numeroCotacao, setNumeroCotacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const primeiroCampo = useRef(null);

  const precisaOrcamento = modo === 'orcamento' || modo === 'ambos';
  const precisaCotacao = modo === 'cotacao' || modo === 'ambos';

  useEffect(() => {
    if (!aberto) return;
    setModo('orcamento');
    setNumped('');
    setNumeroCotacao('');
    setEnviando(false);
    // Foco no campo: quem abre isto já tem o número na mão (ou na tela do ERP
    // ao lado) e vai digitar. Um toque a menos.
    setTimeout(() => primeiroCampo.current?.focus(), 80);
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return undefined;
    const aoTeclar = (e) => { if (e.key === 'Escape' && !enviando) onClose(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, enviando, onClose]);

  if (!aberto) return null;

  const podeEnviar = !enviando
    && (!precisaOrcamento || numped.trim())
    && (!precisaCotacao || numeroCotacao.trim());

  const enviar = async () => {
    if (!podeEnviar) return;
    setEnviando(true);
    try {
      await axios.post(`${API}/orcamento/enviar`, {
        conversa_id: conversaId,
        tipo: modo,
        numped: precisaOrcamento ? numped.trim() : undefined,
        numero_cotacao: precisaCotacao ? numeroCotacao.trim() : undefined,
      });
      Swal.fire({
        icon: 'success',
        title: modo === 'cotacao' ? 'Cotação enviada' : (modo === 'ambos' ? 'Orçamento e cotação enviados' : 'Orçamento enviado'),
        timer: 1300,
        showConfirmButton: false,
      });
      onEnviado?.();
      onClose();
    } catch (e) {
      // O erro vem do comercial com a frase que o vendedor reconhece do portal
      // ("Orçamento não encontrado", "Você não tem permissão..."). Nada foi
      // enviado pro cliente quando isto aparece.
      alertarErro(e, 'Não deu pra enviar');
    } finally {
      setEnviando(false);
    }
  };

  const campo = (ref, valor, setValor, rotulo) => (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
        {rotulo}
      </span>
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        value={valor}
        disabled={enviando}
        onChange={(e) => setValor(e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => { if (e.key === 'Enter') enviar(); }}
        style={{
          width: '100%', padding: '8px 10px', borderRadius: 6,
          border: '1px solid var(--portal-border)', background: 'var(--portal-bg)',
          color: 'var(--portal-text)', fontSize: '1rem',
        }}
      />
    </label>
  );

  return (
    <div
      onClick={() => !enviando && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 20040, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--portal-surface)', color: 'var(--portal-text)',
          border: '1px solid var(--portal-border)', borderRadius: 10,
          padding: 16, width: '100%', maxWidth: 380,
        }}
      >
        <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Enviar pelo WhatsApp</div>
        <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 12 }}>
          {nomeDoCliente || 'esta conversa'}
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {MODOS.map((m) => (
            <button
              key={m.v}
              type="button"
              disabled={enviando}
              onClick={() => setModo(m.v)}
              style={{
                flex: 1, padding: '7px 4px', borderRadius: 8, cursor: enviando ? 'default' : 'pointer',
                fontSize: '0.8rem', fontWeight: modo === m.v ? 700 : 400,
                border: `1px solid ${modo === m.v ? '#3D8B37' : 'var(--portal-border)'}`,
                background: modo === m.v ? 'rgba(61,139,55,0.14)' : 'transparent',
                color: 'var(--portal-text)',
              }}
            >
              <i className={`bi ${m.icone}`} style={{ display: 'block', marginBottom: 2 }} />
              {m.t}
            </button>
          ))}
        </div>

        {precisaOrcamento && campo(primeiroCampo, numped, setNumped, 'Número do orçamento')}
        {precisaCotacao && campo(precisaOrcamento ? null : primeiroCampo, numeroCotacao, setNumeroCotacao, 'Número da cotação')}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={enviando}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={enviar}
            disabled={!podeEnviar}
            className="btn btn-sm"
            style={{
              background: podeEnviar ? '#3D8B37' : 'var(--portal-border)',
              color: '#fff', border: 'none', minWidth: 110,
            }}
          >
            {enviando
              ? <><span className="screen-loader__spinner" style={{ width: 13, height: 13, borderWidth: 2, display: 'inline-block', marginRight: 6, verticalAlign: -2 }} />Gerando…</>
              : <><i className="bi bi-send me-1" />Enviar</>}
          </button>
        </div>

        {/* Gerar o PDF do orçamento leva alguns segundos (o ERP monta), e da
            cotação leva mais (é renderizada). Sem dizer isso, o "Gerando…"
            parado parece travado. */}
        {enviando && (
          <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)', marginTop: 8 }}>
            O PDF está sendo gerado no comercial. Pode levar alguns segundos.
          </div>
        )}
      </div>
    </div>
  );
}
